/**
 * Relative-time formatting — one implementation, several presentations.
 *
 * Ten near-copies of this arithmetic had accumulated across the app
 * (relDate, relTime ×3, formatRelativeTime ×4, formatRelativeShort,
 * formatTime, relDays). Three were byte-identical; the rest differed only in
 * where they roll over to an absolute date and whether they say "just now".
 * Those differences are real product choices — a notification list wants a
 * date after a week, a sponsor modal wants "3mo ago" — so this keeps them as
 * options rather than flattening every surface to one wording.
 *
 * The point is that the arithmetic, the invalid-date handling and the
 * clock-skew guard live in exactly one place.
 */

export interface RelativeTimeOptions {
  /** Days before falling back to an absolute date. Default 30. */
  rolloverDays?: number;
  /** Show "just now" under a minute. Default true. When false, sub-minute
   *  ages render as "1m ago" (some surfaces never want the phrase). */
  justNow?: boolean;
  /** What to show past `rolloverDays`. Default a short "Mar 4". */
  absolute?: 'short' | 'locale' | 'months';
}

const MINUTE = 60_000;
const DAY = 86_400_000;

/** Whole days elapsed, or null when the input isn't a usable date. */
export function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / DAY);
}

/**
 * "just now" / "5m ago" / "3h ago" / "2d ago" / "Mar 4".
 *
 * Returns '' for null, undefined or an unparseable date — every caller
 * renders the result inline, so a bad timestamp must produce nothing rather
 * than "NaN ago".
 */
export function relativeTime(
  input: string | number | Date | null | undefined,
  options: RelativeTimeOptions = {},
): string {
  const { rolloverDays = 30, justNow = true, absolute = 'short' } = options;

  if (input === null || input === undefined || input === '') return '';
  const date = input instanceof Date ? input : new Date(input);
  const ms = date.getTime();
  if (Number.isNaN(ms)) return '';

  // Never negative: a clock skew of a few seconds between the server's
  // timestamp and the browser would otherwise render "-1m ago".
  const elapsed = Math.max(0, Date.now() - ms);
  const mins = Math.floor(elapsed / MINUTE);

  if (mins < 1) return justNow ? 'just now' : '1m ago';
  if (mins < 60) return `${mins}m ago`;

  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < rolloverDays) return `${days}d ago`;

  if (absolute === 'months') return `${Math.floor(days / 30)}mo ago`;
  if (absolute === 'locale') return date.toLocaleDateString();
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Day-granularity phrasing: "today" / "yesterday" / "4d ago".
 *
 * Used where the exact hour is noise — a pitch sent this morning and one
 * sent at midnight are both "today" as far as the pipeline is concerned.
 */
export function relativeDays(iso: string | null | undefined): string {
  const d = daysSince(iso);
  if (d === null) return '';
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  return `${d}d ago`;
}
